import { Note } from "@/schema"
import { create } from "zustand"

import type { DraftAttachment } from "@/components/notes/note-attachments"

interface NoteDraft {
  noteId: string
  note: Note
  selectedHouseholdId: string | null
  selectedContactId: string | null
  selectedBusinessId: string | null
  attachments: DraftAttachment[]
  isDirty: boolean
  onSaved?: () => void
}

type NoteDraftUpdater = Partial<NoteDraft> | ((draft: NoteDraft) => NoteDraft)

interface NoteDialogStore {
  drafts: Record<string, NoteDraft>
  initializeDraft: (id: string, draft: Partial<NoteDraft>) => void
  updateDraft: (id: string, updates: NoteDraftUpdater) => void
  removeDraft: (id: string) => void
}

const DEFAULT_NOTE: Note = {}

const DEFAULT_DRAFT: NoteDraft = {
  noteId: "",
  note: DEFAULT_NOTE,
  selectedHouseholdId: null,
  selectedContactId: null,
  selectedBusinessId: null,
  attachments: [],
  isDirty: false,
  onSaved: undefined,
}

export const useNoteDialogStore = create<NoteDialogStore>((set) => ({
  drafts: {},
  initializeDraft: (id, draft) =>
    set((state) => {
      if (state.drafts[id]) {
        return state
      }

      return {
        drafts: {
          ...state.drafts,
          [id]: {
            ...DEFAULT_DRAFT,
            ...draft,
            note: { ...DEFAULT_DRAFT.note, ...(draft.note ?? {}) },
            noteId: draft.noteId ?? DEFAULT_DRAFT.noteId,
          },
        },
      }
    }),
  updateDraft: (id, updates) =>
    set((state) => {
      const existing = state.drafts[id]
      if (!existing) return state

      const nextDraft =
        typeof updates === "function"
          ? updates(existing)
          : {
              ...existing,
              ...updates,
              note: updates.note ? { ...existing.note, ...updates.note } : existing.note,
            }

      return {
        drafts: {
          ...state.drafts,
          [id]: nextDraft,
        },
      }
    }),
  removeDraft: (id) =>
    set((state) => {
      if (!state.drafts[id]) {
        return state
      }

      const updatedDrafts = { ...state.drafts }
      delete updatedDrafts[id]

      return {
        drafts: updatedDrafts,
      }
    }),
}))
